import { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import type { Condominio } from '@/lib/index';

interface CreateCondominioData {
  nome: string;
  indirizzo?: string;
  cf?: string;
  iban?: string;
  amministratore?: string;
  anno_costruzione?: number;
  num_unita?: number;
  // Campi anagrafici estesi
  assicurazione_compagnia?: string;
  assicurazione_polizza?: string;
  ascensore_ditta?: string;
  ascensore_matricola?: string;
  impianti_termici_ditta?: string;
  verifica_elevatori?: string;
  verifica_acqua?: string;
  note?: string;
}

interface UpdateCondominioData extends Partial<CreateCondominioData> {}

export function useCondomini() {
  const [condomini, setCondomini] = useState<Condominio[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  const { user, isSuperAdmin } = useAuth();
  const mountedRef = useRef(true);

  const fetchCondomini = useCallback(async () => {
    if (!user) {
      if (mountedRef.current) {
        setCondomini([]);
        setLoading(false);
      }
      return;
    }

    try {
      setLoading(true);
      setError(null);

      let query = supabase.from('condomini').select('*');

      if (!isSuperAdmin) {
        query = query.eq('user_id', user.id);
      }

      const { data, error: fetchError } = await query.order('nome', { ascending: true });

      if (fetchError) {
        if (mountedRef.current) {
          setError(fetchError.message);
          setCondomini([]);
        }
        return;
      }

      if (mountedRef.current) {
        setCondomini((data || []) as Condominio[]);
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      if (mountedRef.current) {
        setError(msg);
        setCondomini([]);
      }
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, [user, isSuperAdmin]);

  const createCondominio = useCallback(async (condominioData: CreateCondominioData): Promise<Condominio | null> => {
    try {
      if (!user) throw new Error('Non autenticato');

      // ── Controllo CF duplicato PRIMA dell'INSERT ──
      const cfTrimmed = condominioData.cf?.trim();
      if (cfTrimmed) {
        const { data: existing } = await supabase
          .from('condomini')
          .select('id, nome')
          .eq('user_id', user.id)
          .ilike('cf', cfTrimmed)
          .maybeSingle();

        if (existing) {
          throw new Error(
            `Il codice fiscale "${cfTrimmed}" è già associato al condominio "${existing.nome}". Verificare i dati prima di procedere.`
          );
        }
      }

      // ── Controllo nome duplicato (stesso utente) ──
      const nomeTrimmed = condominioData.nome?.trim();
      if (nomeTrimmed) {
        const { data: existingNome } = await supabase
          .from('condomini')
          .select('id, nome')
          .eq('user_id', user.id)
          .ilike('nome', nomeTrimmed)
          .maybeSingle();

        if (existingNome) {
          throw new Error(
            `Esiste già un condominio con il nome "${existingNome.nome}". Usare un nome diverso o modificare quello esistente.`
          );
        }
      }

      const { data, error: createError } = await supabase
        .from('condomini')
        .insert([{ ...condominioData, user_id: user.id }])
        .select()
        .single();

      if (createError) {
        // Intercetta errore UNIQUE dal DB come fallback
        if (createError.code === '23505') {
          throw new Error(
            `Il codice fiscale "${cfTrimmed}" è già presente. Impossibile creare un duplicato.`
          );
        }
        throw createError;
      }

      if (mountedRef.current) {
        setCondomini((prev) => [...prev, data as Condominio].sort((a, b) => a.nome.localeCompare(b.nome)));
      }
      return data as Condominio;
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      if (mountedRef.current) {
        setError(msg);
      }
      throw err;
    }
  }, [user]);

  const updateCondominio = useCallback(async (id: string, updates: UpdateCondominioData): Promise<Condominio | null> => {
    try {
      const { data, error: updateError } = await supabase
        .from('condomini')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (updateError) throw updateError;

      if (mountedRef.current) {
        setCondomini((prev) => prev.map((c) => (c.id === id ? (data as Condominio) : c)));
      }
      return data as Condominio;
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      if (mountedRef.current) {
        setError(msg);
      }
      return null;
    }
  }, []);

  const deleteCondominio = useCallback(async (id: string): Promise<void> => {
    try {
      const { error: deleteError } = await supabase
        .from('condomini')
        .delete()
        .eq('id', id);

      if (deleteError) throw deleteError;

      if (mountedRef.current) {
        setCondomini((prev) => prev.filter((c) => c.id !== id));
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      if (mountedRef.current) {
        setError(msg);
      }
      throw err;
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    fetchCondomini();

    return () => {
      mountedRef.current = false;
    };
  }, [fetchCondomini]);

  return {
    condomini,
    loading,
    error,
    fetchCondomini,
    createCondominio,
    updateCondominio,
    deleteCondominio,
  };
}
